import { compare, Operation } from "fast-json-patch";
import { Path } from "react-hook-form";

/**
 * Wrapper to add additional functionality and improvements to fast-json-patch's compare method.
 * Allows for treating specified fields as atomic. If any atomic field has changed,
 * its value in the old object is set to `null` before comparison, ensuring
 * the patch replaces the entire field rather than generating granular changes.
 *
 * Also handles Date type fields properly as the normal behaviour produces unexpected results
 *
 * @template T - The type of the objects being compared.
 * @param oldObj - The original object.
 * @param newObj - The updated object to compare against the original.
 * @param atomicPaths - An array of field paths to treat as atomic.
 * @returns An array of JSON Patch operations representing the changes.
 */
export function betterJsonPatchCompare<T extends object>(
  oldObj: T,
  newObj: T,
  atomicPaths: Path<T>[] = [],
  ignoredPaths: Path<T>[] = [],
): Operation[] {
  // structuredClone required to properly handle null values
  const oldCopy = structuredClone(oldObj);
  const newCopy = structuredClone(newObj);

  for (const path of atomicPaths) {
    const oldValue = getNestedValue(oldCopy, path);
    const newValue = getNestedValue(newCopy, path);

    // If both are undefined / null, set them both to undefined
    // otherwise if one is null and the other is undefined a change will be detected
    if (oldValue == null && newValue == null) {
      setNestedValue(oldCopy, path, undefined);
      setNestedValue(newCopy, path, undefined);
      continue;
    }
    if (JSON.stringify(oldValue) === JSON.stringify(newValue)) continue;

    // Only newValue is null so force a remove operation
    // Pass in undefined so instead of null so a change is detected.
    if (newValue == null) {
      setNestedValue(newCopy, path, undefined);
      continue;
    }

    // value is being replaced (both sides have a defined value)
    // Setting old val to null ensures the whole object will have a replace operation
    if (oldValue !== undefined) {
      setNestedValue(oldCopy, path, null);
    }
  }

  for (const path of ignoredPaths) {
    setNestedValue(oldCopy, path, null);
    setNestedValue(newCopy, path, null);
  }

  // Comparison with objects that include properties of Type Date produces unexpected results
  // We stringify them and then parse them as follows so the dates become strings
  const oldJsonCompatible = JSON.parse(JSON.stringify(oldCopy));
  const newJsonCompatible = JSON.parse(JSON.stringify(newCopy));

  return compare(oldJsonCompatible, newJsonCompatible);
}

function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((current, key) => current?.[key], obj);
}

function setNestedValue(obj: any, path: string, value: any): void {
  const keys = path.split(".");
  const lastKey = keys.pop();
  const target = keys.reduce((current, key) => {
    if (current[key] === undefined) {
      current[key] = {};
    }
    return current[key];
  }, obj);
  target[lastKey] = value;
}
